import { useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { useMobile } from "lib/@components/mobile"
import { ConditionalTradesSection } from "./conditional-trades-section"
import { validateConditionalTrade } from "./validate-conditional-trade"

/**
 * Per-skill-line configuration dialog. Opened from the SkillChip on the skill map view
 * when the conditional-trades feature flag is enabled. Lets the user:
 *
 *   - review the line's default trade (the one used when no conditional matches)
 *   - add / reorder / edit / delete the ordered conditional trade assignments
 *
 * The dialog hands the final shape back via `onResult` and lets the caller persist;
 * that keeps controller wiring out of this presentational layer.
 */
export function ConfigureSkillRoutingDialog({
    skillName,
    skillCode,
    initialTradeId,
    defaultTradeName,
    initialConditionals,
    tradeOptions,
    onResult,
    onClose,
    open,
}) {
    const isMobile = useMobile()
    const tradeId = initialTradeId || null
    const [conditionals, setConditionals] = useState(initialiseConditionals)

    const tradeOption = tradeOptions.find(isSelectedTrade) || null
    const defaultTradeLabel = tradeOption?.[1] || defaultTradeName || "No default trade"
    const hasDefaultTrade = !!tradeId || !!defaultTradeName
    const problemsById = useMemo(buildProblemsById, [conditionals])
    const hasDefaultTradeProblem = conditionals.length > 0 && !hasDefaultTrade
    const hasProblems = problemsById.size > 0 || hasDefaultTradeProblem
    const hasChanges = useMemo(buildHasChanges, [conditionals, initialConditionals])
    const canSave = !hasProblems && hasChanges

    const headerLabel = useMemo(buildHeaderLabel, [defaultTradeLabel, hasDefaultTrade, conditionals.length])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
            <DialogTitle>
                <Stack spacing={0.5}>
                    <Typography variant="h6">
                        Configure routing: {skillCode ? `${skillName} (${skillCode})` : skillName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {headerLabel}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">Default trade</Typography>
                        <Box
                            data-cy="default-trade-picker"
                            sx={{
                                px: 1.5,
                                py: 1.25,
                                border: 1,
                                borderColor: hasDefaultTradeProblem ? "error.main" : "divider",
                                borderRadius: 1,
                                bgcolor: "background.neutral",
                            }}
                        >
                            <Typography variant="body2">{defaultTradeLabel}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Tasks for this skill use the default trade when no conditional matches.
                        </Typography>
                    </Stack>

                    <Divider />

                    <ConditionalTradesSection
                        conditionals={conditionals}
                        tradeOptions={tradeOptions}
                        problemsById={problemsById}
                        onConditionalsChange={setConditionals}
                    />

                    <If value={hasProblems}>
                        <Alert severity="warning" data-cy="conditional-trade-blocking">
                            Fix the highlighted routing details before saving. Conditional routing requires a default
                            trade, and each conditional needs a name, at least one complete condition, a combinator, and
                            a target trade.
                        </Alert>
                    </If>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-cy="conditional-trade-cancel">
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave} data-cy="conditional-trade-save">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )

    function initialiseConditionals() {
        return normaliseInitial(initialConditionals)
    }

    function isSelectedTrade(t) {
        return t[0] === tradeId
    }

    function buildHeaderLabel() {
        if (conditionals.length === 0) return `Maps to ${defaultTradeLabel}.`
        const count = conditionals.length
        const suffix = count === 1 ? "1 conditional" : `${count} conditionals`
        if (!hasDefaultTrade) return `No default trade · ${suffix}.`
        return `Maps to ${defaultTradeLabel} · ${suffix}.`
    }

    function buildProblemsById() {
        const result = new Map()
        for (const c of conditionals) {
            const problems = validateConditionalTrade(c)
            if (problems.length > 0) result.set(c.id, problems)
        }
        return result
    }

    function buildHasChanges() {
        const initial = {
            conditionals: normaliseInitial(initialConditionals),
        }
        const current = {
            conditionals,
        }
        return JSON.stringify(initial) !== JSON.stringify(current)
    }

    function handleSave() {
        if (!canSave) return
        onResult({ tradeId, conditionalTrades: conditionals })
        onClose?.()
    }
}

function normaliseInitial(list) {
    if (!Array.isArray(list)) return []
    // Strip GraphQL __typename fields and re-stamp sortOrder by array index so the UI
    // never displays an inconsistent ordering after a partial load.
    return list
        .map((c, i) => ({
            id: c.id,
            name: c.name || "",
            conditions: (c.conditions || []).map((cond) => ({
                field: cond.field || "",
                operator: cond.operator || "",
                value: cond.value === undefined ? null : cond.value,
            })),
            combinator: c.combinator === "or" ? "or" : "and",
            tradeId: c.tradeId || null,
            sortOrder: i,
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder)
}
